import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLightbulb, 
  faRocket, 
  faShareAlt, 
  faPlayCircle, 
  faDownload, 
  faUsers, 
  faLock, 
  faUnlock, 
  faFilePowerpoint,
  faStar,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';

const VisualBriefAbout = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-slate-900 to-teal-900 py-8 px-2 sm:px-4 overflow-x-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Header with Parallax Effect */}
      <header className="relative z-10 flex flex-col items-center mb-4 text-center bg-black border-b border-teal-500/50 pb-8 transform transition-all duration-1000 hover:scale-105">
        <div className="text-4xl lg:text-8xl mb-2">🎯</div>
        <h1 className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent mb-4 animate-pulse">
          Discover VisualBrief
        </h1>
        <p className="text-gray-300 text-sm l:text-lg max-w-3xl mx-auto leading-relaxed animate-fade-in-up">
          Unlock the power of visual storytelling! Create decks that dazzle, share stories that stick, and engage audiences like never before.
        </p>
      </header>

      {/* Interactive Hero Section */}
      <section className="relative z-10 flex flex-col items-center justify-center min-h-[40vh] mb-4 text-center px-4 animate-slide-in">
        <div className="grid grid-cols-3 md:grid-cols-3 gap-2 mx-auto">
          <StatCard number="30+" icon={faLightbulb} label="Versatile Templates" />
          <StatCard number="∞" icon={faShareAlt} label="Sharing Options" />
          <StatCard number="100%" icon={faStar} label="User Satisfaction" />
        </div>
        <p className="text-white/90 text-m lg:text-lg font-medium max-w-4xl mx-auto mt-8 leading-relaxed animate-fade-in">
          Join thousands of creators transforming ideas into unforgettable experiences. From pitch decks that close deals to event invites that wow guests—VisualBrief makes it effortless and exciting!
        </p>
      </section>

      {/* Engaging Features Grid with Hover Animations */}
      <section className="relative z-10 mx-auto mb-6 px-4">
        <h2 className="text-xl  lg:text-2xl font-bold text-teal-300 text-center mb-4 animate-fade-in-up">
          Why You'll Love VisualBrief ✨
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2 place-items-center">
          <FeatureCard
            icon={faFilePowerpoint}
            title="Craft Epic Decks"
            description="Whip up masters with 3-25 slides bursting with flair. Add covers, descriptions, and metadata that make your content pop! 💥"
            delay={0}
          />
          <FeatureCard
            icon={faLightbulb}
            title="Purpose-Packed Templates"
            description="From boardroom battles to birthday bashes—pick from 30+ sparks of inspiration across categories like biz, education, and events. Ignite your creativity! 🔥"
            delay={100}
          />
          <FeatureCard
            icon={faShareAlt}
            title="Share Like a Pro"
            description="Go public, whisper to VIPs, or gatekeep for groups. Control the vibe with smart privacy toggles. Your story, your rules! 🔒"
            delay={200}
          />
          <FeatureCard
            icon={faPlayCircle}
            title="Present with Panache"
            description="Hit play and watch magic unfold. Real-time status keeps you in the spotlight—prepared decks shine brightest! 🎬"
            delay={300}
          />
          <FeatureCard
            icon={faDownload}
            title="Download & Distribute"
            description="One-click exports for offline awesomeness. Toggle downloads to keep your content flowing freely (or not)! 📤"
            delay={400}
          />
          <FeatureCard
            icon={faChartLine}
            title="Insights That Inspire"
            description="Spy on views and downloads with easy logs. Turn data into your secret sauce for even better decks next time! 📈"
            delay={500}
          />
        </div>
      </section>

      {/* Storytelling How It Works with Timeline */}
      <section className="relative z-10 max-w-5xl mx-auto mb-6 px-4">
        <h2 className="text-xl lg:text-2xl font-bold text-teal-300 text-center mb-4 animate-fade-in-up">
          Your Journey in 3 Thrilling Steps
        </h2>
        <div className="relative">
          <div className="absolute left-1/2 transform -translate-x-1/2 w-full h-1 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full -z-10"></div>
          <div className="space-y-12">
            {[
              {
                step: '1️⃣ Dream It',
                title: 'Spark Your Deck Master',
                desc: 'Dive into a world of purposes—biz pitches, class outlines, party invites. Set slides, add zing with titles & descriptions. Your vision starts here!',
                icon: '💡'
              },
              {
                step: '2️⃣ Build It',
                title: 'Customize & Conquer',
                desc: 'Slide into action: edit, rearrange, lock it down. Theme with books, prep for prime time. Make it yours, make it roar!',
                icon: '🔨'
              },
              {
                step: '3️⃣ Launch It',
                title: 'Share, Wow, Repeat',
                desc: 'Blast to libraries, tease publicly, or DM the elite. Present live, track the buzz, tweak for triumph. The stage is yours!',
                icon: '🚀'
              }
            ].map((item, index) => (
              <div
                key={index}
                className={`flex items-center justify-center flex-col lg:flex-row gap-6 p-6 bg-slate-800/50 border border-teal-500/30 rounded-2xl backdrop-blur-sm hover:bg-slate-700/50 transition-all duration-500 hover:scale-105 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } animate-slide-in-up`}
                style={{ animationDelay: `${index * 200}ms` }}
              >
                <div className="flex-shrink-0 text-2xl lg:text-4xl">{item.icon}</div>
                <div className="text-center lg:text-left">
                  <h3 className="text-xl lg:text-2xl font-bold text-white mb-3 flex items-center gap-2 justify-center lg:justify-start">
                    {item.step} {item.title}
                  </h3>
                  <p className="text-gray-300 text:m lg:text-lg leading-relaxed max-w-md">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fun Purposes Carousel-Style Grid */}
      <section className="relative z-10 max-w-6xl mx-auto mb-6 px-4">
        <h2 className="text-xl  lg:text-2xl font-bold text-teal-300 text-center mb-4 animate-fade-in-up">
          Unleash Creativity in Every Corner 🎨
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-center animate-slide-in">
          {[
            { title: '💼 Biz & Corporate', desc: 'Pitch like a boss!' },
            { title: '📚 Education', desc: 'Teach with flair!' },
            { title: '🎉 Events', desc: 'Party invites that pop!' },
            { title: '📣 Marketing', desc: 'Promote with punch!' },
            { title: '👤 Personal', desc: 'Your story, amplified!' },
            { title: '🖼️ Portfolios', desc: 'Showcase your art!' },
            { title: '🏛️ Government', desc: 'Inform with impact!' },
            { title: '🏠 Property', desc: 'Sell dreams visually!' }
          ].map((purpose, index) => (
            <div
              key={index}
              className="group bg-teal-600/20 border border-teal-500/30 rounded-xl p-4 shadow-md hover:bg-teal-500/40 hover:scale-110 transition-all duration-300 cursor-pointer"
            >
              <div className="text-3xl mb-2 group-hover:scale-125 transition-transform">{purpose?.title?.split(' ')[0] || ''}</div>
              <h4 className="font-semibold text-teal-300 group-hover:text-white mb-1">{purpose?.title?.split(' ').slice(1).join(' ') || ''}</h4>
              <p className="text-sm text-gray-400 group-hover:text-gray-200">{purpose.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Slider Simulation */}
      <section className="relative z-10 max-w-4xl mx-auto mb-6 px-4">
        <h2 className="text-xl  lg:text-2xl font-bold text-teal-300 text-center mb-4 animate-fade-in-up">
          What Creators Are Raving About ⭐
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { quote: '"VisualBrief turned my chaotic ideas into a killer pitch deck. Closed the deal in record time!"', author: 'Alex R., Startup Founder' },
            { quote: '"From lesson plans to event flyers—it\'s my go-to for everything visual. So addictive!"', author: 'Jordan L., Teacher & Event Planner' },
            { quote: '"The sharing logs? Game-changer. I know exactly who\'s loving my portfolio!"', author: 'Sam K., Designer' }
          ].map((testimonial, index) => (
            <div
              key={index}
              className="bg-slate-800/50 border border-teal-500/30 rounded-2xl p-6 text-center hover:bg-slate-700/50 transition-all duration-300 hover:rotate-1"
              style={{ animationDelay: `${index * 200}ms` }}
            >
              <p className="text-gray-300 italic mb-4 leading-relaxed">"{testimonial.quote}"</p>
              <div className="flex items-center justify-center gap-2">
                <FontAwesomeIcon icon={faStar} className="text-yellow-400" />
                <span className="font-semibold text-teal-300">{testimonial.author}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Explosive CTA */}
      <section className="relative z-10 text-center mb-4 px-4 animate-explode-in">
        <div className="text-5xl sm:text-7xl mb-6 animate-bounce infinite">⚡</div>
        <h2 className="text-xl  lg:text-2xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent mb-6">
          Your Epic Deck Awaits—Let's Ignite!
        </h2>
        <p className="text-white/90 text-lg mb-4 max-w-2xl mx-auto">Why wait? Dive in and craft something legendary today.</p>
        <Link
          to="/visualbrief-myvb"
          className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white rounded-full shadow-2xl transition-all duration-300 font-bold text-lg transform hover:scale-110 hover:shadow-teal-500/50"
        >
          <FontAwesomeIcon icon={faRocket} className="group-hover:rotate-12 transition-transform" />
          Blast Off Now!
        </Link>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-black border-t border-teal-500/50 text-white py-8 mt-auto">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6 animate-fade-in-up">
          <div className="text-sm lg:text-base text-yellow-200 text-center md:text-left">
            © 2025 Think5Whys Global DeepTech Solutions. Fueling visual revolutions worldwide.
          </div>
          <div className="flex flex-wrap gap-6 text-sm lg:text-base justify-center md:justify-end">
            <Link to="/about-us" className="text-white hover:text-teal-400 transition-colors duration-200 hover:underline">About Us</Link>
            <Link to="/privacy-policy" className="text-white hover:text-teal-400 transition-colors duration-200 hover:underline">Privacy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-teal-400 transition-colors duration-200 hover:underline">Terms</Link>
            <Link to="/contact-us" className="text-white hover:text-teal-400 transition-colors duration-200 hover:underline">Contact</Link>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.6s ease-out forwards;
        }
        .animate-slide-in-up {
          animation: slide-in 0.6s ease-out forwards;
        }
        @keyframes explode-in {
          0% { transform: scale(0.5); opacity: 0; }
          50% { transform: scale(1.1); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-explode-in {
          animation: explode-in 1s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

// Enhanced Stat Card
const StatCard = ({ number, icon, label }) => (
  <div className="group bg-slate-800/50 backdrop-blur-sm border border-teal-500/30 rounded-2xl p-6 text-center hover:bg-teal-500/20 transition-all duration-300 hover:scale-110">
    <FontAwesomeIcon icon={icon} className="text-4xl text-teal-400 mb-3 group-hover:scale-125 transition-transform" />
    <div className="text-3xl font-bold text-white mb-2">{number}</div>
    <p className="text-gray-300 font-medium">{label}</p>
  </div>
);

// Enhanced Feature Card with Delay
const FeatureCard = ({ icon, title, description, delay }) => (
  <div 
    className="group relative bg-slate-800/50 backdrop-blur-sm border border-teal-500/30 rounded-2xl p-6 shadow-lg hover:shadow-teal-500/50 transition-all duration-500 hover:scale-105 overflow-hidden animate-fade-in-up"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-teal-400/0 to-teal-400/10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
    <FontAwesomeIcon
      icon={icon}
      className="text-xl lg:text-2xl text-teal-400 mb-4 relative z-10 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500"
    />
    <h3 className="text-xl lg:text-2xl font-bold text-white mb-3 relative z-10">{title}</h3>
    <p className="text-gray-300 leading-relaxed relative z-10">{description}</p>
  </div>
);

export default VisualBriefAbout;